import { IconButton, ListItemIcon, ListItemText, Menu, MenuItem, Typography, tooltipClasses } from "@mui/material";
import { createElement, useMemo, useState } from "react";
import { TbDotsVertical, TbLicense } from "react-icons/tb";

import { softColors } from "@/constants/colors";
import { NoMaxWidthTooltip } from "@/pages/DriverStatus.tsx/components/DriverStatusColumnTooltip";
import HotelStatusTooltip from "@/pages/HotelPlanning/arrival/components/table/components/HotelStatusTooltip";
import { useTrackingActions, useTrackingSelectedRows } from "@/stores/use-tracking-table";
import { EXTERNAL_DRIVER } from "@/types/global";
import { IPlanPassenger, Task } from "@/types/tasks.types";

import { TrackingClickActionNames, trackingActions } from "./TrackingActionsColumn";

function TrackingActionHeader({ allRows }: { allRows: Task<IPlanPassenger<"api">>[] }) {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

    const open = Boolean(anchorEl);
    const selectedRowIds = useTrackingSelectedRows();
    const { updateShowBulkRegisterDialog, updateShowBulkCancelDialog } = useTrackingActions();
    const selectedRows = useMemo(
        () =>
            allRows
                .filter((row) => selectedRowIds.includes(row.id))
                .filter((row) => row.driverId !== EXTERNAL_DRIVER.id),
        [allRows, selectedRowIds]
    );
    const infos: Partial<Record<TrackingClickActionNames, Set<string>>> = useMemo(
        () =>
            selectedRows.reduce(
                (prev, { uetdsId, id }) => {
                    if (!uetdsId) {
                        prev[TrackingClickActionNames.UETDS_REGISTER].add(id);
                    } else {
                        prev[TrackingClickActionNames.UETDS_CANCEL].add(id);
                    }
                    return prev;
                },
                {
                    [TrackingClickActionNames.UETDS_REGISTER]: new Set<string>(),
                    [TrackingClickActionNames.UETDS_CANCEL]: new Set<string>(),
                }
            ),
        [selectedRows]
    );

    const handleRegister = () => {
        const ids =
            infos[TrackingClickActionNames.UETDS_REGISTER] &&
            Array.from(infos[TrackingClickActionNames.UETDS_REGISTER].values());
        if (!ids || !ids.length) return;
        updateShowBulkRegisterDialog({ ids });
    };

    const handleCancel = () => {
        const ids =
            infos[TrackingClickActionNames.UETDS_CANCEL] &&
            Array.from(infos[TrackingClickActionNames.UETDS_CANCEL].values());
        if (!ids || !ids.length) return;
        updateShowBulkCancelDialog({ ids });
    };

    const clickActions: Partial<Record<TrackingClickActionNames, { cb: () => void }>> = useMemo(
        () => ({
            [TrackingClickActionNames.UETDS_REGISTER]: {
                cb: handleRegister,
            },
            [TrackingClickActionNames.UETDS_CANCEL]: {
                cb: handleCancel,
            },
        }),
        [handleRegister, handleCancel]
    );

    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const visibleActions = useMemo(
        () => trackingActions.filter((action) => !action.showInMenu && clickActions[action.onClick]),
        [clickActions]
    );
    const menuActions = useMemo(
        () => trackingActions.filter((action) => action.showInMenu && clickActions[action.onClick]),
        [clickActions]
    );

    return (
        <div className="flex items-center ">
            {visibleActions.map((action) => (
                <VisibleAction
                    key={action.label}
                    action={action}
                    infos={infos}
                    handleClick={clickActions[action.onClick]?.cb!}
                />
            ))}
            {menuActions.length > 0 && (
                <>
                    <IconButton onClick={handleClick} size="small" className=" !w-[30px] grow-0 shrink-0 !h-[30px]">
                        <TbDotsVertical size={18} />
                    </IconButton>
                    <Menu
                        sx={{
                            ":root": {},
                        }}
                        classes={{
                            paper: "!min-w-[178.58px]",
                            list: "!min-w-[178.58px]",
                        }}
                        anchorEl={anchorEl}
                        open={open}
                        onClose={handleClose}
                        onClick={handleClose}
                    >
                        {menuActions.map((action) => (
                            <MenuItem
                                disabled={!infos[action.onClick]?.size}
                                key={action.label}
                                onClick={clickActions[action.onClick]?.cb}
                                sx={{}}
                            >
                                {/* {createElement(action.icon)}
                                <span className="ml-auto">{action.label}</span> */}
                                <ListItemIcon>{createElement(action.icon, { color: action.color })}</ListItemIcon>
                                <ListItemText>{action.label}</ListItemText>
                                <Typography variant="body2" sx={{ color: "text.secondary", paddingLeft: 3 }}>
                                    {infos[action.onClick]?.size ? infos[action.onClick]?.size.toString() : "0"}
                                </Typography>
                            </MenuItem>
                        ))}
                    </Menu>
                </>
            )}
        </div>
    );
}

function VisibleAction({
    action,
    infos,
    handleClick,
}: {
    action: (typeof trackingActions)[number];
    infos: Partial<Record<TrackingClickActionNames, Set<string>>>;
    handleClick: () => void;
}) {
    return (
        <NoMaxWidthTooltip
            key={action.label}
            arrow
            sx={{
                [`& .${tooltipClasses.tooltip}`]: {
                    backgroundColor: "white",
                    border: "none",
                    padding: "0px !important",
                    fontSize: 12,
                    maxWidth: "200px !important",
                },
            }}
            title={
                <HotelStatusTooltip
                    status={{
                        ...softColors.indigo,
                        component: () => TbLicense,
                        tooltip: {
                            title: action.label,
                            description: infos[action.onClick]?.size
                                ? infos[action.onClick]?.size.toString() + " görev için UETDS oluştur"
                                : "Yapılabilecek işlem yok.",
                        },
                    }}
                />
            }
        >
            <IconButton
                onClick={handleClick}
                className=" !w-[23px] !h-[23px] grow-0 shrink-0"
                color="primary"
                size="small"
                sx={{
                    color: `${action.color}.main`,
                    "&:hover": { backgroundColor: `${action.color}.lighter` },
                }}
            >
                {createElement(action.icon, { size: 14 })}
            </IconButton>
        </NoMaxWidthTooltip>
    );
}

export default TrackingActionHeader;
